// AiUsageCalls — pen H-06: search, filter chips, totals for the filter, the
// sortable call list (time newest first by default), paging and CSV
// export. A row opens the call's detail drawer over the list. Pure
// projection of the call-list read; every change is a new route.
//
// Beyond the frame (design gap): a "Type" column names the call site
// (chat round, OCR, part generation, …), the call-type filter, and chips
// for the id filters (entity, file, tool) a link can carry.

export { AiUsageCalls };

import {
    AI_USAGE_PAGE_SIZE,
    type AiUsageCallList,
    type AiUsageCallRow,
    type AiUsageSort,
    aiUsageCostFilters,
    aiUsageOutcomeFilters,
    aiUsageSiteTypes,
} from "@bilda/app/agentCallAdmin";
import { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type FormEvent, useState } from "react";
import {
    callTypeLabel,
    costFilterLabel,
    formatCount,
    formatDayTime,
    formatInt,
    formatMoney,
    formatSeconds,
    outcomeFilterLabel,
    outcomeLabel,
    siteDetailLabel,
    totalTokens,
} from "../../app/aiUsage/format.js";
import {
    type AiUsageFilterKey,
    type AiUsageRoute,
    withFilter,
    withoutFilter,
} from "../../app/aiUsage/route.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Select } from "../Select/Select.js";
import type { AiUsageRead } from "./AiUsagePage.js";
import { LoadFailed, OutcomeBadge, TableSkeleton } from "./AiUsageStates.js";
// The shared frame styles first, so this view's rules win ties.
import "./AiUsage.css";
import "./AiUsageCalls.css";

type Props = {
    readonly route: AiUsageRoute;
    readonly calls: AiUsageRead<AiUsageCallList>;
    readonly csvHref: string;
    readonly onNavigate: (route: AiUsageRoute) => void;
    readonly onRetry: () => void;
};

const ALL = "__all";

const Search = ({
    route,
    onNavigate,
}: {
    readonly route: AiUsageRoute;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const { t } = useLingui();
    // View-ephemeral: the text being typed, until Enter.
    const [draft, setDraft] = useState(route.filters.q ?? "");
    const submit = (e: FormEvent) => {
        e.preventDefault();
        const q = draft.trim();
        onNavigate(
            q === "" ? withoutFilter(route, "q") : withFilter(route, "q", q),
        );
    };
    return (
        <form className="ai-usage__search" onSubmit={submit}>
            <Icon name="search" size="sm" />
            <input
                className="ai-usage__search-input"
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={t`Request ID or conversation`}
                aria-label={t`Search calls by request ID, call ID or conversation`}
            />
        </form>
    );
};

const Filters = ({
    route,
    list,
    onNavigate,
}: {
    readonly route: AiUsageRoute;
    readonly list: AiUsageCallList | undefined;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const { t, i18n } = useLingui();
    const f = route.filters;
    const set = (
        key: AiUsageFilterKey,
        v: string,
        allowed?: readonly string[],
    ) =>
        onNavigate(
            v === ALL || (allowed !== undefined && !allowed.includes(v))
                ? withoutFilter(route, key)
                : key === "callType"
                  ? withFilter(
                        route,
                        "callType",
                        aiUsageSiteTypes.find((x) => x === v),
                    )
                  : key === "outcome"
                    ? withFilter(
                          route,
                          "outcome",
                          aiUsageOutcomeFilters.find((x) => x === v),
                      )
                    : key === "cost"
                      ? withFilter(
                            route,
                            "cost",
                            aiUsageCostFilters.find((x) => x === v),
                        )
                      : withFilter(route, key, v),
        );
    // A chosen value stays listed even when the period has no calls for it.
    const withCurrent = (
        options: readonly { value: string; label: string }[],
        current: string | undefined,
    ) =>
        current === undefined || options.some((o) => o.value === current)
            ? options
            : [...options, { value: current, label: current }];
    const facets = list?.facets;
    const all = { value: ALL, label: t`All` };
    return (
        <div className="ai-usage__filters">
            <span className="ai-usage__chip">
                <Select
                    prefix={t`User:`}
                    ariaLabel={t`Filter by user`}
                    value={f.user ?? ALL}
                    onValueChange={(v) => set("user", v)}
                    options={[
                        all,
                        ...withCurrent(
                            (facets?.users ?? []).map((u) => ({
                                value: u.id,
                                label: u.label ?? u.id,
                            })),
                            f.user,
                        ),
                    ]}
                />
            </span>
            <span className="ai-usage__chip">
                <Select
                    prefix={t`Workspace:`}
                    ariaLabel={t`Filter by workspace`}
                    value={f.workspace ?? ALL}
                    onValueChange={(v) => set("workspace", v)}
                    options={[
                        all,
                        ...withCurrent(
                            (facets?.workspaces ?? []).map((w) => ({
                                value: w.id,
                                label: w.label ?? t`Untitled workspace`,
                            })),
                            f.workspace,
                        ),
                    ]}
                />
            </span>
            <span className="ai-usage__chip">
                <Select
                    prefix={t`Type:`}
                    ariaLabel={t`Filter by call type`}
                    value={f.callType ?? ALL}
                    onValueChange={(v) => set("callType", v, aiUsageSiteTypes)}
                    options={[
                        all,
                        ...aiUsageSiteTypes.map((s) => ({
                            value: s,
                            label: i18n._(callTypeLabel(s)),
                        })),
                    ]}
                />
            </span>
            <span className="ai-usage__chip">
                <Select
                    prefix={t`Model:`}
                    ariaLabel={t`Filter by model`}
                    value={f.model ?? ALL}
                    onValueChange={(v) => set("model", v)}
                    options={[
                        all,
                        ...withCurrent(
                            (facets?.models ?? []).map((m) => ({
                                value: m,
                                label: m,
                            })),
                            f.model,
                        ),
                    ]}
                />
            </span>
            <span className="ai-usage__chip">
                <Select
                    prefix={t`Outcome:`}
                    ariaLabel={t`Filter by outcome`}
                    value={f.outcome ?? ALL}
                    onValueChange={(v) =>
                        set("outcome", v, aiUsageOutcomeFilters)
                    }
                    options={[
                        all,
                        ...aiUsageOutcomeFilters.map((o) => ({
                            value: o,
                            label: i18n._(outcomeFilterLabel(o)),
                        })),
                    ]}
                />
            </span>
            <span className="ai-usage__chip">
                <Select
                    prefix={t`Cost:`}
                    ariaLabel={t`Filter by cost`}
                    value={f.cost ?? ALL}
                    onValueChange={(v) => set("cost", v, aiUsageCostFilters)}
                    options={[
                        { value: ALL, label: t`Any` },
                        ...aiUsageCostFilters.map((c) => ({
                            value: c,
                            label: i18n._(costFilterLabel(c)),
                        })),
                    ]}
                />
            </span>
        </div>
    );
};

// Chips for the id filters a link carries (entity, file, tool, search).
const IdChips = ({
    route,
    onNavigate,
}: {
    readonly route: AiUsageRoute;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const { t } = useLingui();
    const f = route.filters;
    const chips: readonly {
        key: AiUsageFilterKey;
        label: string;
        value: string;
    }[] = [
        ...(f.q === undefined
            ? []
            : [{ key: "q" as const, label: t`Search`, value: f.q }]),
        ...(f.entity === undefined
            ? []
            : [{ key: "entity" as const, label: t`Used`, value: f.entity }]),
        ...(f.file === undefined
            ? []
            : [{ key: "file" as const, label: t`File`, value: f.file }]),
        ...(f.tool === undefined
            ? []
            : [{ key: "tool" as const, label: t`Tool`, value: f.tool }]),
    ];
    if (chips.length === 0) return null;
    return (
        <div className="ai-usage__id-chips">
            {chips.map((c) => (
                <span key={c.key} className="ai-usage__id-chip">
                    <span>{c.label}:</span>
                    <code className="ai-usage__id">{c.value}</code>
                    <IconButton
                        size="sm"
                        variant="ghost"
                        label={t`Remove filter ${c.label}`}
                        onClick={() => onNavigate(withoutFilter(route, c.key))}
                    >
                        <Icon name="close" size="xs" />
                    </IconButton>
                </span>
            ))}
        </div>
    );
};

const SortHeader = ({
    route,
    sort,
    label,
    numeric = true,
    onNavigate,
}: {
    readonly route: AiUsageRoute;
    readonly sort: AiUsageSort;
    readonly label: string;
    readonly numeric?: boolean;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const active = route.sort === sort;
    const { callId: _closed, ...rest } = route;
    return (
        <th
            scope="col"
            className={numeric ? "ai-usage__num" : undefined}
            aria-sort={
                active
                    ? route.dir === "asc"
                        ? "ascending"
                        : "descending"
                    : "none"
            }
        >
            <button
                type="button"
                className="ai-usage__sort"
                onClick={() =>
                    onNavigate({
                        ...rest,
                        sort,
                        dir: active && route.dir === "desc" ? "asc" : "desc",
                        page: 0,
                    })
                }
            >
                {label}
                {active ? (
                    <Icon
                        name={
                            route.dir === "asc" ? "chevron-up" : "chevron-down"
                        }
                        size="xs"
                    />
                ) : null}
            </button>
        </th>
    );
};

const Row = ({
    c,
    route,
    onNavigate,
}: {
    readonly c: AiUsageCallRow;
    readonly route: AiUsageRoute;
    readonly onNavigate: (route: AiUsageRoute) => void;
}) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const open = () => onNavigate({ ...route, callId: c.callId });
    const noUsage = c.usageType === "usage_unknown";
    const tokensText = `${formatInt(loc, c.inputTokens + c.cacheReadTokens + c.cacheWriteTokens)} / ${c.outputTokens === 0 ? "—" : formatInt(loc, c.outputTokens)}`;
    const user =
        c.user?.label ??
        c.user?.id ??
        (c.initiatorType === "system" ? t`Background` : t`Unknown`);
    const workspace =
        c.workspace === null
            ? t`Shared`
            : (c.workspace.label ?? t`Untitled workspace`);
    return (
        <tr
            className={`ai-usage__row--link${route.callId === c.callId ? " ai-usage__row--open" : ""}`}
            onClick={open}
        >
            <td className="ai-usage__time">
                <button
                    type="button"
                    className="ai-usage__row-button"
                    onClick={open}
                >
                    {formatDayTime(loc, c.startedAt)}
                </button>
            </td>
            <td
                className="ai-usage__name"
                title={i18n._(siteDetailLabel(c.site))}
            >
                {i18n._(siteDetailLabel(c.site))}
                {c.attempt > 1 ? (
                    <span className="ai-usage__attempt">{t`try ${c.attempt}`}</span>
                ) : null}
            </td>
            <td className="ai-usage__name" title={user}>
                {user}
            </td>
            <td className="ai-usage__name" title={workspace}>
                {workspace}
            </td>
            <td className="ai-usage__model">
                <code title={c.modelId}>{c.modelId}</code>
            </td>
            <td className="ai-usage__num" title={tokensText}>
                {noUsage ? "—" : tokensText}
            </td>
            <td className="ai-usage__num ai-usage__strong">
                {c.costMicroUsd === null ? (
                    noUsage ? (
                        "—"
                    ) : (
                        <span
                            title={t`The model is not in the rate table`}
                        >{t`unknown`}</span>
                    )
                ) : (
                    formatMoney(loc, c.costMicroUsd)
                )}
            </td>
            <td className="ai-usage__num">
                {c.ttftMs === null ? "—" : formatSeconds(loc, c.ttftMs)}
            </td>
            <td className="ai-usage__num">
                {formatSeconds(loc, c.durationMs)}
            </td>
            <td
                className="ai-usage__outcome-cell"
                title={i18n._(outcomeLabel(c.outcome, c.toolCalls))}
            >
                <OutcomeBadge outcome={c.outcome} toolCalls={c.toolCalls} />
            </td>
        </tr>
    );
};

const AiUsageCalls = ({
    route,
    calls,
    csvHref,
    onNavigate,
    onRetry,
}: Props) => {
    const { t, i18n } = useLingui();
    const loc = i18n.locale;
    const list = Remote.match<
        AiUsageCallList,
        unknown,
        AiUsageCallList | undefined
    >({
        notAsked: () => undefined,
        loading: () => undefined,
        reloading: (prev) => prev,
        loaded: (v) => v,
        failed: () => undefined,
    })(calls);
    const first = route.page * AI_USAGE_PAGE_SIZE;
    const page = (n: number) => {
        const { callId: _closed, ...rest } = route;
        onNavigate({ ...rest, page: n });
    };
    return (
        <div className="ai-usage__calls">
            <div className="ai-usage__toolbar">
                <Search
                    key={route.filters.q ?? ""}
                    route={route}
                    onNavigate={onNavigate}
                />
                <Filters route={route} list={list} onNavigate={onNavigate} />
            </div>
            <IdChips route={route} onNavigate={onNavigate} />
            {calls._tag === "Remote.Failed" ? (
                <LoadFailed onRetry={onRetry} />
            ) : list === undefined ? (
                <TableSkeleton />
            ) : (
                <>
                    <div className="ai-usage__totals-row">
                        <dl className="ai-usage__totals">
                            <div>
                                <dt>{t`Calls`}</dt>
                                <dd>{formatInt(loc, list.totals.calls)}</dd>
                            </div>
                            <div>
                                <dt>{t`Tokens`}</dt>
                                <dd>
                                    {formatCount(loc, totalTokens(list.totals))}
                                </dd>
                            </div>
                            <div>
                                <dt>{t`Cost`}</dt>
                                <dd>
                                    {formatMoney(loc, list.totals.costMicroUsd)}
                                </dd>
                            </div>
                            <div>
                                <dt>{t`Median`}</dt>
                                <dd>
                                    {list.totals.medianDurationMs === null
                                        ? "—"
                                        : formatSeconds(
                                              loc,
                                              list.totals.medianDurationMs,
                                          )}
                                </dd>
                            </div>
                            <div>
                                <dt>{t`Errors`}</dt>
                                <dd>{formatInt(loc, list.totals.failed)}</dd>
                            </div>
                        </dl>
                        <a className="ai-usage__export" href={csvHref} download>
                            <Icon name="download" size="sm" />
                            {t`Export CSV`}
                        </a>
                    </div>
                    {list.rows.length === 0 ? (
                        <p className="ai-usage__empty">{t`No calls match these filters in this period.`}</p>
                    ) : (
                        <div className="ai-usage__table-wrap">
                            <table className="ai-usage__table ai-usage__table--calls">
                                <thead>
                                    <tr>
                                        <SortHeader
                                            route={route}
                                            sort="time"
                                            label={t`Time`}
                                            numeric={false}
                                            onNavigate={onNavigate}
                                        />
                                        <th scope="col">{t`Type`}</th>
                                        <th scope="col">{t`User`}</th>
                                        <th scope="col">{t`Workspace`}</th>
                                        <th scope="col">{t`Model`}</th>
                                        <SortHeader
                                            route={route}
                                            sort="tokens"
                                            label={t`Tokens in / out`}
                                            onNavigate={onNavigate}
                                        />
                                        <SortHeader
                                            route={route}
                                            sort="cost"
                                            label={t`Cost`}
                                            onNavigate={onNavigate}
                                        />
                                        <SortHeader
                                            route={route}
                                            sort="ttft"
                                            label={t`1st token`}
                                            onNavigate={onNavigate}
                                        />
                                        <SortHeader
                                            route={route}
                                            sort="duration"
                                            label={t`Duration`}
                                            onNavigate={onNavigate}
                                        />
                                        <th scope="col">{t`Outcome`}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list.rows.map((c) => (
                                        <Row
                                            key={c.callId}
                                            c={c}
                                            route={route}
                                            onNavigate={onNavigate}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className="ai-usage__pager">
                        <span>
                            {list.total === 0
                                ? t`0 calls`
                                : t`${formatInt(loc, first + 1)}–${formatInt(loc, first + list.rows.length)} of ${formatInt(loc, list.total)}`}
                        </span>
                        <span className="ai-usage__pager-buttons">
                            <IconButton
                                variant="ghost"
                                label={t`Previous page`}
                                disabled={route.page === 0}
                                onClick={() => page(route.page - 1)}
                            >
                                <Icon name="chevron-left" size="sm" />
                            </IconButton>
                            <IconButton
                                variant="ghost"
                                label={t`Next page`}
                                disabled={
                                    first + list.rows.length >= list.total
                                }
                                onClick={() => page(route.page + 1)}
                            >
                                <Icon name="chevron-right" size="sm" />
                            </IconButton>
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};
